import {
  createCurbAndGutterBreakline,
  createJerseyBarrierBreakline,
  createRoadCrownBreakline,
  createWorkspace,
  deleteWorkspace,
  deleteReference,
  listBreaklines,
  listReferences,
  listWorkspaces,
  setMobileMappingMissionReference,
  type Breakline,
  type Client,
  type Workspace,
} from '@data-access/geospatial/fx-workspace-service';
import { UnreachableCaseError } from 'ts-essentials';
import { returnOrThrow } from '@trimble/http-utils';

import type {
  FxBreakline,
  FxBreaklineType,
} from './fx-workspace-breakline.types';

import type {
  FxWorkspaceRepository,
  SyncFxWorkspaceReferencesInput,
} from './fx-workspace-repository';
import type { CreateFxWorkspaceInput, FxWorkspace } from './fx-workspace.types';

const DEFAULT_PAGE_SIZE = 100;
const MAX_PAGES = 100;

const CLIENT_NAME_HEADER = 'Trimble-FieldSystems-ClientName';
const CLIENT_VERSION_HEADER = 'Trimble-FieldSystems-ClientVersion';

function toNumber(value: number | string): number {
  return typeof value === 'number' ? value : Number(value);
}

function hasNextPage(
  rawPageLength: number,
  fetchedCount: number,
  totalItems: number
): boolean {
  return rawPageLength === DEFAULT_PAGE_SIZE && fetchedCount < totalItems;
}

async function fetchAllPages<T>(
  fetchPage: (pageIndex: number) => Promise<{
    items: readonly T[];
    totalItems: number | string;
  }>
): Promise<T[]> {
  const items: T[] = [];
  let pageIndex = 0;
  let fetchedCount = 0;
  let hasMore = true;

  while (hasMore) {
    if (pageIndex >= MAX_PAGES) {
      throw new Error(
        `Exceeded maximum of ${MAX_PAGES} pages while listing FX workspace data`
      );
    }

    const page = await fetchPage(pageIndex);
    fetchedCount += page.items.length;
    items.push(...page.items);

    hasMore = hasNextPage(
      page.items.length,
      fetchedCount,
      toNumber(page.totalItems)
    );
    pageIndex += 1;
  }

  return items;
}

function getClientHeaders(
  clientName: string,
  clientVersion: string
): {
  [CLIENT_NAME_HEADER]: string;
  [CLIENT_VERSION_HEADER]: string;
} {
  return {
    [CLIENT_NAME_HEADER]: clientName,
    [CLIENT_VERSION_HEADER]: clientVersion,
  };
}

function mapWorkspaceResponse(response: Workspace): FxWorkspace {
  return {
    id: response.workspaceTrn,
    name: response.name,
    jobs: [],
  };
}

function mapBreaklineResponse(response: Breakline): FxBreakline {
  return {
    type: response.type,
    name: response.name,
    layerId: response.layerId,
  };
}

export interface ApiFxWorkspaceRepositoryConfig {
  client: Client;
  clientName: string;
  clientVersion: string;
  getTenantTrn(): string | undefined;
}

export class ApiFxWorkspaceRepository implements FxWorkspaceRepository {
  constructor(private readonly config: ApiFxWorkspaceRepositoryConfig) {}

  public async list(): Promise<readonly FxWorkspace[]> {
    const tenantTrn = this.config.getTenantTrn();
    if (tenantTrn === undefined) {
      return [];
    }

    const workspaces = await fetchAllPages(async (pageIndex) => {
      const response = await listWorkspaces({
        client: this.config.client,
        path: { tenantTrn },
        headers: getClientHeaders(
          this.config.clientName,
          this.config.clientVersion
        ),
        query: {
          pageIndex,
          pageSize: DEFAULT_PAGE_SIZE,
        },
      });

      return returnOrThrow(response, 'Failed to list FX workspaces');
    });

    return workspaces
      .filter((workspace) => workspace.isDeleted !== true)
      .map(mapWorkspaceResponse);
  }

  public async create(input: CreateFxWorkspaceInput): Promise<FxWorkspace> {
    const tenantTrn = this.config.getTenantTrn();
    if (tenantTrn === undefined) {
      throw new Error('Tenant TRN is required to create FX workspace');
    }

    const response = await createWorkspace({
      client: this.config.client,
      path: { tenantTrn },
      body: { name: input.name.trim() },
      headers: getClientHeaders(
        this.config.clientName,
        this.config.clientVersion
      ),
    });

    const created = returnOrThrow(response, 'Failed to create FX workspace');
    return mapWorkspaceResponse(created);
  }

  public async delete(workspaceTrn: string): Promise<void> {
    const tenantTrn = this.config.getTenantTrn();
    if (tenantTrn === undefined) {
      throw new Error('Tenant TRN is required to delete FX workspace');
    }

    const response = await deleteWorkspace({
      client: this.config.client,
      path: { tenantTrn, workspaceTrn },
      headers: getClientHeaders(
        this.config.clientName,
        this.config.clientVersion
      ),
    });

    returnOrThrow(response, 'Failed to delete FX workspace');
  }

  public async listReferences(
    workspaceTrn: string
  ): Promise<readonly string[]> {
    const tenantTrn = this.config.getTenantTrn();
    if (tenantTrn === undefined) {
      return [];
    }

    const references = await fetchAllPages(async (pageIndex) => {
      const response = await listReferences({
        client: this.config.client,
        path: { tenantTrn, workspaceTrn },
        headers: getClientHeaders(
          this.config.clientName,
          this.config.clientVersion
        ),
        query: {
          pageIndex,
          pageSize: DEFAULT_PAGE_SIZE,
        },
      });

      return returnOrThrow(response, 'Failed to list FX workspace references');
    });

    return references.map((reference) => reference.refTrn);
  }

  public async addReference(
    workspaceTrn: string,
    refTrn: string
  ): Promise<void> {
    const tenantTrn = this.config.getTenantTrn();
    if (tenantTrn === undefined) {
      throw new Error('Tenant TRN is required to add FX workspace reference');
    }

    const response = await setMobileMappingMissionReference({
      client: this.config.client,
      path: { tenantTrn, workspaceTrn },
      body: { refTrn },
      headers: getClientHeaders(
        this.config.clientName,
        this.config.clientVersion
      ),
    });

    returnOrThrow(response, 'Failed to add FX workspace reference');
  }

  public async syncReferences(
    workspaceTrn: string,
    input: SyncFxWorkspaceReferencesInput
  ): Promise<void> {
    const { toAdd, toRemove } = input;

    for (const refTrn of toAdd) {
      await this.addReference(workspaceTrn, refTrn);
    }

    for (const refTrn of toRemove) {
      await this.removeReference(workspaceTrn, refTrn);
    }
  }

  public async removeReference(
    workspaceTrn: string,
    refTrn: string
  ): Promise<void> {
    const tenantTrn = this.config.getTenantTrn();
    if (tenantTrn === undefined) {
      throw new Error(
        'Tenant TRN is required to remove FX workspace reference'
      );
    }

    const response = await deleteReference({
      client: this.config.client,
      path: { tenantTrn, workspaceTrn, refTrn },
      headers: getClientHeaders(
        this.config.clientName,
        this.config.clientVersion
      ),
    });

    returnOrThrow(response, 'Failed to remove FX workspace reference');
  }

  public async listBreaklines(
    workspaceTrn: string
  ): Promise<readonly FxBreakline[]> {
    const tenantTrn = this.config.getTenantTrn();
    if (tenantTrn === undefined) {
      return [];
    }

    const breaklines = await fetchAllPages(async (pageIndex) => {
      const response = await listBreaklines({
        client: this.config.client,
        path: { tenantTrn, workspaceTrn },
        headers: getClientHeaders(
          this.config.clientName,
          this.config.clientVersion
        ),
        query: {
          pageIndex,
          pageSize: DEFAULT_PAGE_SIZE,
        },
      });

      return returnOrThrow(response, 'Failed to list FX workspace breaklines');
    });

    return breaklines
      .filter((item) => item.isDeleted !== true)
      .map(mapBreaklineResponse);
  }

  public async createBreakline(
    workspaceTrn: string,
    type: FxBreaklineType,
    name: string
  ): Promise<FxBreakline> {
    const tenantTrn = this.config.getTenantTrn();
    if (tenantTrn === undefined) {
      throw new Error(
        'Tenant TRN is required to create FX workspace breakline'
      );
    }

    const headers = getClientHeaders(
      this.config.clientName,
      this.config.clientVersion
    );
    const path = { tenantTrn, workspaceTrn };
    const body = { name: name.trim() };

    const response = await (async () => {
      switch (type) {
        case 'curbAndGutter':
          return createCurbAndGutterBreakline({
            client: this.config.client,
            path,
            body,
            headers,
          });
        case 'roadCrown':
          return createRoadCrownBreakline({
            client: this.config.client,
            path,
            body,
            headers,
          });
        case 'jerseyBarrier':
          return createJerseyBarrierBreakline({
            client: this.config.client,
            path,
            body,
            headers,
          });
        default:
          throw new UnreachableCaseError(type);
      }
    })();

    const created = returnOrThrow(
      response,
      'Failed to create FX workspace breakline'
    );
    return mapBreaklineResponse(created);
  }
}
